/**
Load soundfonts from publicly available urls. Cache based on content hash.

This is actually more of a generalized sha loader and should be evolved as such.

*/

{
  DEFAULT_SPRITE_SHA
  DEFAULT_SYNTH_SHA
  GENERAL_USER_GS_SHA
  LEGACY_SYNTH_SHA
} from ../const.civet

{ urlSafeSHA256 } from @danielx/lib
{ getCache } from ./fs.civet

{ rand } from ./util.civet

// List of fonts
// http://gmsynths.blogspot.com/2016/

// Github Soundblaster list
// https://github.com/DriverPackSolution/DP_Sound_Creative/tree/master/Creative/AllXP/CR25.1/Addon

type SoundFontChoice = {
  name: string
  sha: string
}

type SpritesheetChoice = { name: string, sha: string }
export type SpritesheetChoices = SpritesheetChoice[]

fonts := {
  Ct2mgm: "https://raw.githubusercontent.com/DriverPackSolution/DP_Sound_Creative/master/Creative/AllXP/CR25.1/Addon/Ct2mgm.sf2"
  Ct4mgm: "https://raw.githubusercontent.com/DriverPackSolution/DP_Sound_Creative/master/Creative/AllXP/CR25.1/Addon/Ct4mgm.sf2"
  Ct8mgm: "https://raw.githubusercontent.com/DriverPackSolution/DP_Sound_Creative/master/Creative/AllXP/CR25.1/Addon/Ct8mgm.sf2"
  VintageDreams: "https://raw.githubusercontent.com/FluidSynth/fluidsynth/master/sf2/VintageDreamsWaves-v2.sf2"
  // Our own copy on purpose: the GeneralUser GS license asks that nobody hotlink
  // schristiancollins.com's download files, and a bundled app should not depend on
  // someone else's uptime. app/assets-protocol.js serves this from disk offline.
  GeneralUserGS: "https://danielx.net/composer/sf/GeneralUser-GS.sf2"
  default: "https://danielx.net/composer/sf/default.sf2"
  legacy: "https://danielx.net/composer/sf/legacy.sf2"
} as Record<string, string>

shas := {
  "ujwLwBnzFSVrrjlCVJ2KvVJxO-n6YahfEQ82ikcFSjw": [
    "https://danielx.net/composer/sf/default.sf2"
  ]
  "UhMrK4OZTwBn06Zrk7Txtn1T_4wNO-kNOfI_krRYXN0": [
    "https://raw.githubusercontent.com/FluidSynth/fluidsynth/master/sf2/VintageDreamsWaves-v2.sf2"
  ],
  "z6S1iZ8RggiyqYtcwN01qB2UtaxLRORlIwjC1aoNZG8": [
    "https://raw.githubusercontent.com/DriverPackSolution/DP_Sound_Creative/master/Creative/AllXP/CR25.1/Addon/Ct2mgm.sf2"
  ],
  "bEKepHacjexwXm92b2GU_BTj2EYjaClrAaB2jWaescU": [
    "https://raw.githubusercontent.com/DriverPackSolution/DP_Sound_Creative/master/Creative/AllXP/CR25.1/Addon/Ct4mgm.sf2"
  ],
  "U49gzYfiIyCxVTX7eF2im1hTIkVpe6D7I2Vb05mTXQA": [
    "https://raw.githubusercontent.com/DriverPackSolution/DP_Sound_Creative/master/Creative/AllXP/CR25.1/Addon/Ct8mgm.sf2"
  ]
  "ADie7JCdxXfNn8qgnIj7eE9dFtFO6_aAUa9gcZ6lsEM": [
    "https://danielx.net/composer/sf/legacy.sf2"
  ]
  "lXUCjHofWJ9XcPzMjP8nNFZq9AzSbtg2lE6aUVJojP4": [
    "https://danielx.net/composer/sf/GeneralUser-GS.sf2"
  ]
} as Record<string, string[]>

// @ts-ignore
shas[DEFAULT_SPRITE_SHA] = [
  "https://danielx.net/composer/images/default.png"
]

names := {
  "default": DEFAULT_SYNTH_SHA,
  "Paint Composer Classic": DEFAULT_SYNTH_SHA,
  "legacy": LEGACY_SYNTH_SHA,
  "Paint Composer Legacy": LEGACY_SYNTH_SHA,
  "VintageDreams": "UhMrK4OZTwBn06Zrk7Txtn1T_4wNO-kNOfI_krRYXN0",
  "GeneralUser GS": GENERAL_USER_GS_SHA,
  "GeneralUserGS": GENERAL_USER_GS_SHA,
  "Ct2mgm": "z6S1iZ8RggiyqYtcwN01qB2UtaxLRORlIwjC1aoNZG8",
  "Ct4mgm": "bEKepHacjexwXm92b2GU_BTj2EYjaClrAaB2jWaescU",
  "4MB GMGSMT": "bEKepHacjexwXm92b2GU_BTj2EYjaClrAaB2jWaescU",
  "Ct8mgm": "U49gzYfiIyCxVTX7eF2im1hTIkVpe6D7I2Vb05mTXQA"
} as Record<string, string>

SF_CHOICES_CACHE := "soundfont-choices.json"
SS_CHOICES_CACHE := "spritesheet-choices.json"

self :=
  _addSources: =>
    Promise.all Object.keys(fonts).map (name) =>
      if names[name]
        return

      url := fonts[name]

      self.load(url)
      .then (buffer) =>
        self.cache(buffer)
      .then (sha) =>
        names[name] = sha
        mirrors := (shas[sha] ?= [])
        mirrors.push url
    .then =>
      console.log names, shas

  cacheSoundFontChoices: (choices: SoundFontChoice[]) =>
    getCache().write SF_CHOICES_CACHE,
      new Blob [JSON.stringify(choices)],
        type: "application/json"

  readSoundFontChoicesCache: =>
    if blob := await getCache().read SF_CHOICES_CACHE
      blob.json() as Promise<SoundFontChoice[]>
    else
      throw new Error "Not Found"

  cacheSpritesheetChoices: (choices: SoundFontChoice[]) =>
    getCache().write SS_CHOICES_CACHE,
      new Blob [JSON.stringify(choices)],
        type: "application/json"

  readSpritesheetChoicesCache: =>
    if blob := await getCache().read SS_CHOICES_CACHE
      blob.json() as Promise<SpritesheetChoices>
    else
      throw new Error "Not Found"

  byName: (name: string) =>
    sha := names[name]
    if sha
      self.bySha(sha)
    else
      throw new Error "Not known: #{name}"

  bySha: (sha: string): Promise<ArrayBuffer> =>
    getCache().read(sha)
    .then (blob?: Blob) =>
      if blob
        blob.arrayBuffer()
      else
        throw new Error "not found"
    .catch =>
      self.load `https://data.danielx.net/composer/${sha}`
    .then (buffer: ArrayBuffer) =>
      self.cache buffer

      return buffer
    .catch =>
      sources := shas[sha]

      throw new Error `No known sources for asset with SHA ${sha}` unless sources

      url := rand sources
      self.load url
      .then (buffer) =>
        // async
        self.cache buffer

        return buffer

  cache: (buffer: Blob | BlobPart) =>
    let blob: Blob
    // TODO: this check could move inside of urlSafeSHA256 function
    if buffer instanceof Blob
      blob = buffer
    else
      blob = new Blob [buffer]

    urlSafeSHA256(blob)
    .then (sha: string) =>
      fs := getCache()
      fs.read sha
      .then (existing) =>
        if existing
          return sha

        fs.write sha, blob
        .then =>
          return sha

  load: (url: string) =>
    r := await fetch(url)

    if r.ok
      r.arrayBuffer()
    else
      throw new Error "Failed to load sound font"

export default self
export {}
